/**
 * HTML → Markdown converter (via Turndown) with rules for the custom
 * `<msp-password>`, `<inline-image>`, and `<attachment-chip>` nodes.
 */

import TurndownService from 'turndown';
import { tables as gfmTables } from 'turndown-plugin-gfm';

let td: TurndownService | null = null;
function turndown(): TurndownService {
  if (td) return td;
  td = new TurndownService({ headingStyle: 'atx', codeBlockStyle: 'fenced' });
  // GFM pipe-table emitter for plain rectangular tables. Merged-cell tables
  // bypass this via the merged-table rule below.
  td.use(gfmTables);
  td.addRule('msp-password', {
    filter: (node) => (node as Element).tagName === 'MSP-PASSWORD',
    replacement: (content, node) => {
      const el = node as Element;
      const id = el.getAttribute('id') ?? '';
      const label = el.getAttribute('label') ?? '';
      // Prefer the `value` attribute (editor representation). Fall back to the
      // inner text/content if the tag was authored in Markdown form.
      const value = el.getAttribute('value') ?? content ?? '';
      const idAttr = id ? ` id="${escape(id)}"` : '';
      return `<msp-password${idAttr} label="${escape(label)}">${escape(value)}</msp-password>`;
    },
  });
  td.addRule('inline-image', {
    filter: (node) => (node as Element).tagName === 'INLINE-IMAGE',
    replacement: (_content, node) => {
      const el = node as Element;
      const id = el.getAttribute('id') ?? '';
      const attachmentId = el.getAttribute('attachmentId') ?? '';
      const width = el.getAttribute('width') ?? '';
      const alt = el.getAttribute('alt') ?? '';
      const align = el.getAttribute('align') ?? '';
      const idAttr = id ? ` id="${escape(id)}"` : '';
      const widthAttr = width ? ` width="${escape(width)}"` : '';
      const altAttr = alt ? ` alt="${escape(alt)}"` : '';
      const alignAttr = align ? ` align="${escape(align)}"` : '';
      return `<inline-image${idAttr} attachmentId="${escape(attachmentId)}"${widthAttr}${altAttr}${alignAttr}></inline-image>`;
    },
  });
  td.addRule('attachment-chip', {
    filter: (node) => (node as Element).tagName === 'ATTACHMENT-CHIP',
    replacement: (_content, node) => {
      const el = node as Element;
      const id = el.getAttribute('id') ?? '';
      const attachmentId = el.getAttribute('attachmentId') ?? '';
      const filename = el.getAttribute('filename') ?? '';
      const mimetype = el.getAttribute('mimetype') ?? '';
      const idAttr = id ? ` id="${escape(id)}"` : '';
      return `<attachment-chip${idAttr} attachmentId="${escape(attachmentId)}" filename="${escape(filename)}" mimetype="${escape(mimetype)}"></attachment-chip>`;
    },
  });

  // Inline marks without CommonMark equivalents — emit raw HTML so re-import
  // through TipTap's parseHTML is lossless. The export ZIP and backup archive
  // pass through doc.markdown verbatim, so the embedded HTML survives the
  // round-trip end-to-end.
  td.addRule('underline', {
    filter: (node) => (node as Element).tagName === 'U',
    replacement: (content) => `<u>${content}</u>`,
  });
  // TextStyle + Color render as `<span style="color: …">`. ParseHTML reads
  // back from inline style on re-import, so emitting raw HTML is lossless.
  td.addRule('text-style-span', {
    filter: (node) => {
      const el = node as HTMLElement;
      return el.tagName === 'SPAN' && !!el.style?.color;
    },
    replacement: (content, node) => {
      const c = (node as HTMLElement).style.color;
      return `<span style="color: ${escape(c)}">${content}</span>`;
    },
  });
  // Highlight (multicolor) renders as `<mark data-color="…" style="background-color: …">`.
  // Preserve both attributes for round-trip; parseHTML prefers data-color but
  // falls back to inline style.
  td.addRule('mark-highlight', {
    filter: (node) => (node as Element).tagName === 'MARK',
    replacement: (content, node) => {
      const el = node as HTMLElement;
      const bg = el.style?.backgroundColor || el.getAttribute('data-color') || '';
      if (bg) {
        return `<mark data-color="${escape(bg)}" style="background-color: ${escape(bg)}">${content}</mark>`;
      }
      return `<mark>${content}</mark>`;
    },
  });
  td.addRule('superscript', {
    filter: (node) => (node as Element).tagName === 'SUP',
    replacement: (content) => `<sup>${content}</sup>`,
  });
  td.addRule('subscript', {
    filter: (node) => (node as Element).tagName === 'SUB',
    replacement: (content) => `<sub>${content}</sub>`,
  });

  // Block-level alignment lives as inline `style="text-align: …"` on the
  // paragraph or heading element (TipTap TextAlign serialises that way). When
  // present we emit raw HTML for the block; otherwise the default ATX/p rule
  // fires.
  td.addRule('aligned-paragraph', {
    filter: (node) => {
      const el = node as HTMLElement;
      return el.tagName === 'P' && !!el.style?.textAlign;
    },
    replacement: (content, node) => {
      const align = (node as HTMLElement).style.textAlign;
      return `\n\n<p style="text-align: ${escape(align)}">${content}</p>\n\n`;
    },
  });
  td.addRule('aligned-heading', {
    filter: (node) => {
      const el = node as HTMLElement;
      return /^H[1-6]$/.test(el.tagName) && !!el.style?.textAlign;
    },
    replacement: (content, node) => {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();
      const align = el.style.textAlign;
      return `\n\n<${tag} style="text-align: ${escape(align)}">${content}</${tag}>\n\n`;
    },
  });

  // Tables that GFM pipes can't represent — emit outerHTML verbatim so
  // re-import preserves the structure. Fires when any cell has:
  //   - rowspan/colspan > 1 (merged cells), OR
  //   - data-bg (cell background colour from TableCellEx), OR
  //   - data-align with a non-`left` value (cell text alignment).
  // Registered AFTER `gfmTables` so this rule wins for any matching table;
  // plain unstyled rectangular tables continue via GFM pipes.
  td.addRule('merged-table', {
    filter: (node) => {
      if ((node as Element).tagName !== 'TABLE') return false;
      const cells = (node as Element).querySelectorAll('th, td');
      for (const c of Array.from(cells)) {
        const rs = parseInt(c.getAttribute('rowspan') ?? '1', 10);
        const cs = parseInt(c.getAttribute('colspan') ?? '1', 10);
        if (rs > 1 || cs > 1) return true;
        if (c.hasAttribute('data-bg')) return true;
        const a = c.getAttribute('data-align');
        if (a && a !== 'left') return true;
      }
      return false;
    },
    replacement: (_content, node) => `\n\n${(node as Element).outerHTML}\n\n`,
  });
  return td;
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

export function htmlToMarkdown(html: string): string {
  return turndown().turndown(html);
}
